import React, { useState, useEffect } from "react";
import { PencilIcon, CheckIcon, XIcon } from "@heroicons/react/outline";
import Layout from "../../components/Layout/Layout";
import FXTransactionServices from "../../services/fxTransactions";
import fire from "../../utils/Toastr";
import { convert } from "../../utils/convertDate";
import { Switch } from "@headlessui/react";

export default function ExchangeRates() {
  const [rates, setRates] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [newValue, setNewValue] = useState("");
  const [pendingUpdates, setPendingUpdates] = useState([]);
  const [adminType, setAdminType] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingToggles, setLoadingToggles] = useState({});

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState({
    action: "",
    currencyPair: "",
    currentRate: "",
    proposedRate: "",
    requestId: null,
  });

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const ratesResponse =
          await FXTransactionServices.viewFxTransactionRate();

        if (ratesResponse?.data) {
          const relevantRates = ratesResponse.data.filter((rate) =>
            ["EURToNGNRate", "GBPToNGNRate", "USDToNGNRate"].includes(
              rate.applicationParameter
            )
          );
          setRates(relevantRates);
        }
      } catch (error) {
        console.error("Failed to load initial data", error);
      }
    };

    fetchInitialData();

    const userDetails = JSON.parse(sessionStorage.getItem("user"));
    if (userDetails) {
      setAdminType(userDetails.adminType);
    }
  }, []);

  const fetchAllRates = async () => {
    setLoading(true);
    try {
      const response = await FXTransactionServices.viewFxTransactionRate();
      if (response && response.data) {
        const relevantRates = response.data.filter((rate) =>
          ["EURToNGNRate", "GBPToNGNRate", "USDToNGNRate"].includes(
            rate.applicationParameter
          )
        );

        const enhancedRates = relevantRates.map((rate) => ({
          ...rate,
          modifiedBy: rate.modifiedBy || "System",
          isAvailable: rate.isAvailable || false,
        }));

        setRates(enhancedRates);

        const pending = relevantRates.filter(
          (rate) => rate.settingsApprovalStatus === "Initiated"
        );
        setPendingUpdates(pending);
      }
    } catch (error) {
      fire("error", "Failed to fetch exchange rates");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleAvailability = async (rateId, enabled) => {
    setLoadingToggles((prev) => ({ ...prev, [rateId]: true }));

    try {
      const rateToToggle = rates.find((rate) => rate.id === rateId);
      if (!rateToToggle) {
        fire("error", "Currency rate not found");
        return;
      }

      const payload = {
        applicationParameter: rateToToggle.applicationParameter,
        isAvailable: enabled,
      };

      const response =
        await FXTransactionServices.toggleFxTransactionAvailability(payload);

      if (response?.status) {
        setRates((prevRates) =>
          prevRates.map((rate) =>
            rate.id === rateId ? { ...rate, isAvailable: enabled } : rate
          )
        );
        fire(
          "success",
          `${rateToToggle.applicationParameter.replace("ToNGNRate", "")} FX ${
            enabled ? "enabled" : "disabled"
          }`
        );
      } else {
        fire("error", response?.data || "Failed to update FX availability");
      }
    } catch (error) {
      fire(
        "error",
        error.response?.data?.message || "Error updating FX availability"
      );
    } finally {
      setLoadingToggles((prev) => ({ ...prev, [rateId]: false }));
    }
  };

  useEffect(() => {
    fetchAllRates();
  }, []);

  const handleEdit = (rate) => {
    setEditingId(rate.id);
    setNewValue(rate.applicationValue);
  };

  const handleCancel = () => {
    setEditingId(null);
    setNewValue("");
  };

  const handleValueChange = (e) => {
    setNewValue(e.target.value);
  };

  const submitForApproval = async (rate) => {
    if (!newValue || isNaN(newValue)) {
      fire("error", "Please enter a valid number");
      return;
    }

    try {
      const payload = {
        id: rate.id,
        applicationParameter: rate.applicationParameter,
        applicationValue: parseFloat(newValue),
        makerApplicationValue: parseFloat(newValue),
        settingsApprovalStatus: "Initiated",
      };

      const response = await FXTransactionServices.updateFxTransactionRate(
        payload
      );

      if (response && response.status) {
        fire("success", response.message);
        setEditingId(null);
        setNewValue("");
        await fetchAllRates();
      }
    } catch (error) {
      fire("error", "Failed to initiate rate");
    }
  };

  const openApprovalModal = (request, action) => {
    setModalData({
      action,
      currencyPair: request.applicationParameter.replace("ToNGNRate", " → NGN"),
      currentRate: request.applicationValue,
      proposedRate: request.makerApplicationValue,
      requestId: request.id,
    });
    setShowModal(true);
  };

  const handleApproval = async () => {
    try {
      const payload = {
        id: modalData.requestId,
        applicationParameter: modalData.currencyPair.replace(
          " → NGN",
          "ToNGNRate"
        ),
        applicationValue:
          modalData.action === "approve"
            ? modalData.proposedRate
            : modalData.currentRate,
        settingsApprovalStatus:
          modalData.action === "approve" ? "Approved" : "Rejected",
      };

      const response = await FXTransactionServices.updateFxTransactionRate(
        payload
      );

      if (response && response.status) {
        fire(
          "success",
          `Rate ${
            modalData.action === "approve" ? "approved" : "rejected"
          } successfully`
        );
        setShowModal(false);
        await fetchAllRates();
      }
    } catch (error) {
      fire("error", `Failed to ${modalData.action} update`);
    }
  };

  // const getCurrencySymbol = (parameter) => {
  //   switch (parameter) {
  //     case "USDToNGNRate":
  //       return "$";
  //     case "EURToNGNRate":
  //       return "€";
  //     case "GBPToNGNRate":
  //       return "£";
  //     default:
  //       return "₦";
  //   }
  // };

  const getCurrencySymbol = (parameter) => {
    switch (parameter) {
      case "USDToNGNRate":
        return "₦";
      case "EURToNGNRate":
        return "₦";
      case "GBPToNGNRate":
        return "₦";
      default:
        return "₦";
    }
  };

  return (
    <Layout
      title="Exchange Rates"
      pageTag={"fx_transactions"}
      current={"/rates"}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
        <div className="mt-8">
          <h1 className="text-xl font-semibold text-gray-900">
            Currency Exchange Rates
          </h1>

          <div className="mt-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">
              Current Rates
            </h2>
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 rounded-lg">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Currency Pair
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Current Rate
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Proposed Rate
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Last Updated
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Requested By
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {adminType === "Checker" && (
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Availability
                        </th>
                      )}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {rates.map((rate) => {
                    const currencySymbol = getCurrencySymbol(
                      rate.applicationParameter
                    );
                    const isPending =
                      rate.settingsApprovalStatus === "Initiated";

                    return (
                      <tr
                        key={rate.id}
                        className={isPending ? "bg-yellow-50" : ""}
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {rate.applicationParameter.replace(
                            "ToNGNRate",
                            " → NGN"
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {editingId === rate.id ? (
                            <div className="flex items-center">
                              <span className="mr-1">{currencySymbol}</span>
                              <input
                                type="number"
                                value={newValue}
                                onChange={handleValueChange}
                                className="border rounded px-2 py-1 w-24"
                                step="0.0001"
                              />
                            </div>
                          ) : (
                            <span>
                              {currencySymbol}
                              {Number(rate.applicationValue).toLocaleString(
                                "en-NG",
                                {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                }
                              )}
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {isPending ? (
                            <>
                              {currencySymbol}
                              {Number(
                                rate.makerApplicationValue
                              ).toLocaleString("en-NG", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <span
                            className={`px-2 py-1 rounded-full text-xs ${
                              rate.settingsApprovalStatus === "Approved"
                                ? "bg-green-100 text-green-800"
                                : rate.settingsApprovalStatus === "Initiated"
                                ? "bg-yellow-100 text-yellow-800"
                                : "bg-red-100 text-red-800"
                            }`}
                          >
                            {rate.settingsApprovalStatus || "Approved"}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {convert(rate.dateModified || rate.dateCreated)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {rate.modifiedBy?.split("|")[1] || "System"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {editingId === rate.id ? (
                            <div className="flex space-x-2">
                              <button
                                onClick={() => submitForApproval(rate)}
                                className="text-green-600 hover:text-green-900"
                              >
                                <CheckIcon className="h-5 w-5" />
                              </button>
                              <button
                                onClick={handleCancel}
                                className="text-red-600 hover:text-red-900"
                              >
                                <XIcon className="h-5 w-5" />
                              </button>
                            </div>
                          ) : (
                            adminType !== "Checker" && (
                              <button
                                onClick={() => handleEdit(rate)}
                                className="text-indigo-600 hover:text-indigo-900"
                                disabled={isPending}
                              >
                                <PencilIcon className="h-5 w-5" />
                              </button>
                            )
                          )}
                        </td>
                        {adminType === "Checker" && (
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <div className="flex items-center space-x-2">
                              {loadingToggles[rate.id] ? (
                                <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                              ) : (
                                <>
                                  <Switch
                                    checked={rate.isAvailable || false}
                                    onChange={(enabled) =>
                                      handleToggleAvailability(rate.id, enabled)
                                    }
                                    disabled={loadingToggles[rate.id]}
                                    className={`${
                                      rate.isAvailable
                                        ? "bg-green-600"
                                        : "bg-gray-200"
                                    } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500`}
                                  >
                                    <span className="sr-only">
                                      Toggle availability
                                    </span>
                                    <span
                                      className={`${
                                        rate.isAvailable
                                          ? "translate-x-6"
                                          : "translate-x-1"
                                      } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                                    />
                                  </Switch>
                                  <span className="text-sm text-gray-500">
                                    {rate.isAvailable ? "Enabled" : "Disabled"}
                                  </span>
                                </>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {adminType === "Checker" && pendingUpdates.length > 0 && (
            <div className="mt-8">
              <h2 className="text-lg font-medium text-gray-900 mb-4">
                Pending Approvals (Initiated)
              </h2>
              <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 rounded-lg">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Currency Pair
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Current Rate
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Proposed Rate
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Requested By
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Requested At
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {pendingUpdates.map((request) => (
                      <tr key={request.id} className="bg-yellow-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {request.applicationParameter.replace(
                            "ToNGNRate",
                            " → NGN"
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {getCurrencySymbol(request.applicationParameter)}
                          {Number(request.applicationValue).toLocaleString(
                            "en-NG",
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {getCurrencySymbol(request.applicationParameter)}
                          {Number(request.makerApplicationValue).toLocaleString(
                            "en-NG",
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {request.modifiedBy?.split("|")[1] || "System"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {convert(request.dateModified || request.dateCreated)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <div className="flex space-x-2">
                            <button
                              onClick={() =>
                                openApprovalModal(request, "approve")
                              }
                              className="text-green-600 hover:text-green-900"
                            >
                              Approve
                            </button>
                            <button
                              onClick={() =>
                                openApprovalModal(request, "reject")
                              }
                              className="text-red-600 hover:text-red-900"
                            >
                              Reject
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Approval Modal */}
      {showModal && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 transition-opacity"
              aria-hidden="true"
            >
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <span
              className="hidden sm:inline-block sm:align-middle sm:h-screen"
              aria-hidden="true"
            >
              &#8203;
            </span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  Confirm{" "}
                  {modalData.action === "approve" ? "Approval" : "Rejection"}
                </h3>
                <div className="mt-4">
                  <p className="text-sm text-gray-500">
                    Are you sure you want to {modalData.action} this rate
                    change?
                  </p>
                  <div className="mt-4 space-y-2">
                    <p className="text-sm">
                      <span className="font-medium">Currency Pair:</span>{" "}
                      {modalData.currencyPair}
                    </p>
                    <p className="text-sm">
                      <span className="font-medium">Current Rate:</span>{" "}
                      {getCurrencySymbol(
                        modalData.currencyPair.replace(" → NGN", "ToNGNRate")
                      )}
                      {Number(modalData.currentRate).toLocaleString("en-NG", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </p>
                    <p className="text-sm">
                      <span className="font-medium">Proposed Rate:</span>{" "}
                      {getCurrencySymbol(
                        modalData.currencyPair.replace(" → NGN", "ToNGNRate")
                      )}
                      {Number(modalData.proposedRate).toLocaleString("en-NG", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white sm:ml-3 sm:w-auto sm:text-sm ${
                    modalData.action === "approve"
                      ? "bg-green-600 hover:bg-green-700 focus:ring-green-500"
                      : "bg-red-600 hover:bg-red-700 focus:ring-red-500"
                  }`}
                  onClick={handleApproval}
                >
                  {modalData.action === "approve" ? "Approve" : "Reject"}
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
